/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import { FeatureCollection, MultiPolygon, Polygon } from '@turf/helpers';
import { difference, includes, isNil, range } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  LineSegment,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryCursorContainer,
  VictoryGroup,
  VictoryLabel,
  VictoryPortal,
  VictoryScatter,
  VictoryStack,
  VictoryTooltip,
} from 'victory';
import FlightLegServiceAlerts, { ServiceAlert } from '../../../../shared/entities/flightLegServiceAlerts';
import {
  selectFlightLegServiceAlerts,
  selectFlightLegVictoryChartData,
  selectMissionPlanById,
  selectSelectedOverlaidFlightLegId,
} from '../../../../store';
import { PERFORMANCE_OUTLOOK_MAP_ID } from '../../../../store/maps/actions';
import { deleteFlightLegVictoryChartData, setFlightLegVictoryChartData } from '../../../../store/missionPlans/actions';
import { FlightLegChartData } from '../../../../store/missionPlans/reducer';
import {
  MINUTES_IN_DAY,
  MINUTES_IN_FOUR_HOURS,
  MINUTES_IN_HALF_DAY,
  MINUTES_IN_HALF_HOUR,
  MINUTES_IN_HOUR,
  MINUTES_IN_TWO_HOURS,
  minutesToFormattedMinutesStringColonDelimitedFormat,
} from '../../../../utils/DateTimeUtils';
import {
  ServiceAlertType,
  getAltitudeRestrictionForBeam,
  getLandingSegmentType,
  getSegmentColorBySegmentType,
  getSegmentColorDefinitions,
  getSegmentType,
  getTakeoffSegmentType,
} from '../../../../utils/MptFlightPathSegmentingUtils';
import appTheme from '../../../common/theme/appTheme.json';
import { getAltitudeString } from '../../../fleet/MptFlightPath/MarkerDialogContent';
import LineAnnotation from './LineAnnotation';
import ServiceAlertMarker from './ServiceAlertMarker';

interface PerformanceOutlookHorizontalTimelineProps {
  chartWidth: number;
  chartTopPosition: number;
  flightLegId?: number;
  disableTimelineHover?: boolean;
  mapId?: string;
}

const INDEPENDENT_AXIS_DATA = 'Data';
const INDEPENDENT_AXIS_MARKER = 'Marker';
const DEFAULT_TICK_INTERVAL = 15;

export const CHART_TOP_POSITION = 650;
export const CHART_WIDTH = 1500; // Do not change
export const CHART_SIDE_PADDING = 60;
const STACKED_BARS_LENGTH_DX_CHART_WIDTH = 75; // Length in pixels of all the stacked bars (do not change)
const SCATTER_HEIGHT = 50;
const BAR_HEIGHT = 125;
const LINE_LENGTH = 50;

const PerformanceOutlookHorizontalTimeline = (props: PerformanceOutlookHorizontalTimelineProps) => {
  const { chartWidth, disableTimelineHover, flightLegId, mapId } = props;

  const dispatch = useDispatch();
  const serviceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const currentMission = useSelector(selectMissionPlanById, shallowEqual);
  const graphData = useSelector(selectFlightLegVictoryChartData, shallowEqual);
  const [tickInterval, setTickInterval] = useState<number>(DEFAULT_TICK_INTERVAL);
  const [marginalServiceData, setMarginalServiceData] = useState<{ x: string; y: number; color: string }[]>([]);
  const selectedMapId = !isNil(mapId) ? mapId : PERFORMANCE_OUTLOOK_MAP_ID;
  let selectedOverlaidFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(selectedMapId), shallowEqual);
  selectedOverlaidFlightLegId = !isNil(flightLegId) ? flightLegId : selectedOverlaidFlightLegId;

  // Determines color of service alert bar
  const getSegmentColor = useCallback(
    (alert: ServiceAlert | { timeSinceTakeOffMin: number }): string => {
      const timestamp = alert.timeSinceTakeOffMin;

      if (selectedOverlaidFlightLegId && serviceAlerts) {
        if (alert && 'type' in alert && serviceAlerts[selectedOverlaidFlightLegId]?.beams && timestamp !== 0) {
          return getSegmentColorBySegmentType(
            getSegmentType(alert),
            getAltitudeRestrictionForBeam(
              alert.loginBeamName,
              alert.loginBeamNetwork,
              alert.loginBeamMapType,
              serviceAlerts[selectedOverlaidFlightLegId].beams,
            ),
            getSegmentColorDefinitions(),
          );
        } else if (serviceAlerts[selectedOverlaidFlightLegId]?.beams && timestamp === 0) {
          return getSegmentColorBySegmentType(
            getTakeoffSegmentType(serviceAlerts[selectedOverlaidFlightLegId]),
            getAltitudeRestrictionForBeam(
              serviceAlerts[selectedOverlaidFlightLegId].originBeamName,
              serviceAlerts[selectedOverlaidFlightLegId].originBeamNetwork,
              serviceAlerts[selectedOverlaidFlightLegId].originBeamMapType,
              serviceAlerts[selectedOverlaidFlightLegId].beams,
            ),
            getSegmentColorDefinitions(),
          );
        }
      }

      return getSegmentColorDefinitions().mptNominalServiceFlightPathColor;
    },
    [serviceAlerts, selectedOverlaidFlightLegId],
  );

  // Creates takeoff description to be placed in VictoryScatter tooltip
  const getTakeoffDescription = useCallback((): string => {
    let description = '';
    const serviceAlert =
      serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

    // If beams are given within /serviceAlerts response
    if (serviceAlert?.beams?.features?.length) {
      let originInCoverageDescription = `Take off from ${serviceAlert?.origin} (${serviceAlert?.originBeamDisplayName})`;
      const hnaAltitudeString = getEventListHnaAltitudeString(
        serviceAlert.originBeamName,
        serviceAlert.originBeamNetwork,
        serviceAlert.originBeamMapType,
        serviceAlert.beams,
      );
      if (getTakeoffSegmentType(serviceAlert) === 'HNA' && !isNil(hnaAltitudeString)) {
        originInCoverageDescription = `Take off from ${serviceAlert?.origin} (${serviceAlert?.originBeamDisplayName} ${hnaAltitudeString})`;
      }

      description = serviceAlert?.originInCoverage
        ? originInCoverageDescription
        : `Take off from ${serviceAlert?.origin} (No Coverage)`;
    } else {
      // Else no beams
      description = `Take off from ${serviceAlert?.origin} (No Coverage)`;
    }

    return description;
  }, [serviceAlerts, selectedOverlaidFlightLegId]);

  // Creates landing description to be placed in VictoryScatter tooltip
  const getLandingDescription = useCallback((): string => {
    let description = '';
    const serviceAlert =
      serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

    // If beams are given within /serviceAlerts response
    if (serviceAlert?.beams?.features?.length) {
      let destinationInCoverageDescription = `Land at ${serviceAlert?.destination} (${serviceAlert?.destinationBeamDisplayName})`;
      const hnaAltitudeString = getEventListHnaAltitudeString(
        serviceAlert.destinationBeamName,
        serviceAlert.destinationBeamNetwork,
        serviceAlert.destinationBeamMapType,
        serviceAlert.beams,
      );
      if (getLandingSegmentType(serviceAlert) === 'HNA' && !isNil(hnaAltitudeString)) {
        destinationInCoverageDescription = `Land at ${serviceAlert?.destination} (${serviceAlert?.destinationBeamDisplayName} ${hnaAltitudeString})`;
      }

      description = serviceAlert?.destinationInCoverage
        ? destinationInCoverageDescription
        : `Land at ${serviceAlert?.destination} (No Coverage)`;
    } else {
      // Else no beams
      description = `Land at ${serviceAlert?.destination} (No Coverage)`;
    }

    return description;
  }, [serviceAlerts, selectedOverlaidFlightLegId]);

  // Creates service alert description to be placed in VictoryScatter tooltip
  const getAlertDescription = useCallback(
    (
      serviceAlert: ServiceAlert,
      beams: FeatureCollection<MultiPolygon, Polygon>,
      prevServiceAlert?: ServiceAlert,
    ): string => {
      const prevSelectedServiceAlert =
        serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

      const prevAlertLoginBeamInCoverage = !isNil(prevServiceAlert)
        ? prevServiceAlert.loginBeamInCoverage
        : prevSelectedServiceAlert?.originInCoverage;
      const prevAlertLoginBeamType = !isNil(prevServiceAlert)
        ? prevServiceAlert.loginBeamType
        : prevSelectedServiceAlert?.originBeamType;
      let description = '';

      if (serviceAlert.type === ServiceAlertType.HAND_OFF) {
        description = `${serviceAlert.loginBeamType !== serviceAlert.logoutBeamType ? 'Network' : 'Beam'} Handoff ${
          serviceAlert.logoutBeamDisplayName
        } to ${serviceAlert.loginBeamDisplayName}`;
      } else if (
        serviceAlert.type === ServiceAlertType.HNA_ZONE ||
        serviceAlert.type === ServiceAlertType.NO_COVERAGE
      ) {
        //HNA -> Coverage
        if (
          (serviceAlert.logoutBeamType === 'HNA' ||
            serviceAlert.logoutBeamType === 'KA_HNA' ||
            serviceAlert.logoutBeamType === 'HCX_HNA') &&
          serviceAlert.loginBeamType != null &&
          serviceAlert.loginBeamType !== 'HNA' &&
          serviceAlert.loginBeamType !== 'KA_HNA' &&
          serviceAlert.loginBeamType !== 'HCX_HNA'
        ) {
          description = `Enter Coverage (Log into ${serviceAlert.loginBeamDisplayName})`;
        }
        // No Coverage -> HNA
        else if (
          serviceAlert.type === 'NO_COVERAGE' &&
          (serviceAlert.loginBeamType === 'HNA' ||
            serviceAlert.loginBeamType === 'KA_HNA' ||
            serviceAlert.loginBeamType === 'HCX_HNA') &&
          serviceAlert.logoutBeamType == null &&
          serviceAlert.loginBeamInCoverage === true
        ) {
          const hnaAltitudeString = getEventListHnaAltitudeString(
            serviceAlert.loginBeamName,
            serviceAlert.loginBeamNetwork,
            serviceAlert.loginBeamMapType,
            beams,
          );

          description = !isNil(hnaAltitudeString)
            ? `Enter ${serviceAlert.loginBeamDisplayName} (${hnaAltitudeString})`
            : `Enter ${serviceAlert.loginBeamDisplayName}`;
        }
        // HNA -> HNA
        else if (
          (serviceAlert.logoutBeamType === 'HNA' ||
            serviceAlert.logoutBeamType === 'KA_HNA' ||
            serviceAlert.logoutBeamType === 'HCX_HNA') &&
          (serviceAlert.loginBeamType === 'HNA' ||
            serviceAlert.loginBeamType === 'KA_HNA' ||
            serviceAlert.loginBeamType === 'HCX_HNA') &&
          serviceAlert.loginBeamInCoverage &&
          prevAlertLoginBeamInCoverage
        ) {
          const hnaAltitudeString = getEventListHnaAltitudeString(
            serviceAlert.loginBeamName,
            serviceAlert.loginBeamNetwork,
            serviceAlert.loginBeamMapType,
            beams,
          );

          description = !isNil(hnaAltitudeString)
            ? `Enter ${serviceAlert.loginBeamDisplayName} (${hnaAltitudeString})`
            : `Enter ${serviceAlert.loginBeamDisplayName}`;
        }

        // HNA -> No Coverage
        else if (
          serviceAlert.type === 'NO_COVERAGE' &&
          serviceAlert.loginBeamInCoverage === false &&
          (prevAlertLoginBeamType === 'HNA' ||
            prevAlertLoginBeamType === 'KA_HNA' ||
            prevAlertLoginBeamType === 'HCX_HNA')
        ) {
          description = 'Enter No Coverage';
        }

        // Coverage -> No Coverage
        else if (
          serviceAlert.type === 'NO_COVERAGE' &&
          !serviceAlert.loginBeamInCoverage &&
          serviceAlert.logoutBeamType !== 'HNA' &&
          serviceAlert.logoutBeamType !== 'KA_HNA' &&
          serviceAlert.logoutBeamType !== 'HCX_HNA'
        ) {
          description = 'Enter No Coverage';
        }

        // No Coverage -> Coverage
        else if (
          serviceAlert.type === 'NO_COVERAGE' &&
          !prevAlertLoginBeamInCoverage &&
          serviceAlert.loginBeamInCoverage
        ) {
          description = `Enter Coverage (Log into ${serviceAlert.loginBeamDisplayName})`;
        }
        // Coverage -> HNA
        else if (
          serviceAlert.logoutBeamType !== 'HNA' &&
          serviceAlert.logoutBeamType !== 'KA_HNA' &&
          serviceAlert.logoutBeamType !== 'HCX_HNA' &&
          serviceAlert.logoutBeamType != null &&
          (serviceAlert.loginBeamType === 'HNA' ||
            serviceAlert.loginBeamType === 'KA_HNA' ||
            serviceAlert.loginBeamType === 'HCX_HNA')
        ) {
          const hnaAltitudeString = getEventListHnaAltitudeString(
            serviceAlert.loginBeamName,
            serviceAlert.loginBeamNetwork,
            serviceAlert.loginBeamMapType,
            beams,
          );

          description = !isNil(hnaAltitudeString)
            ? `Enter ${serviceAlert.loginBeamDisplayName} (${hnaAltitudeString})`
            : `Enter ${serviceAlert.loginBeamDisplayName}`;
        }
      }

      return description;
    },
    [serviceAlerts, selectedOverlaidFlightLegId],
  );

  // Specifies axis tick values (tick every hour)
  const getTickIntervalsList = useCallback((): number[] => {
    const serviceAlert =
      serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

    const totalMins = serviceAlert?.durationMin ? serviceAlert?.durationMin : -1;
    let intervalArray = [0];
    // Populate array with tick locations with respect to interval
    for (let i = tickInterval; i < totalMins; i += tickInterval) {
      intervalArray.push(i);
    }

    return intervalArray;
  }, [serviceAlerts, selectedOverlaidFlightLegId, tickInterval]);

  const getTickInterval = useCallback((): number => {
    const serviceAlert =
      serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

    const totalMins = serviceAlert?.durationMin ? serviceAlert?.durationMin : -1;
    const divisions = totalMins > MINUTES_IN_TWO_HOURS ? 16 : 12;
    let interval = totalMins;

    if (totalMins > MINUTES_IN_DAY) {
      interval = MINUTES_IN_DAY / divisions;
    } else if (totalMins > MINUTES_IN_HALF_DAY) {
      interval = MINUTES_IN_HALF_DAY / divisions;
    } else if (totalMins > MINUTES_IN_FOUR_HOURS) {
      interval = MINUTES_IN_FOUR_HOURS / divisions;
    } else if (totalMins > MINUTES_IN_TWO_HOURS) {
      interval = 5;
    } else if (totalMins > MINUTES_IN_HOUR) {
      interval = 5;
    } else if (totalMins > MINUTES_IN_HALF_HOUR) {
      interval = MINUTES_IN_HALF_HOUR / divisions;
    }

    return interval;
  }, [serviceAlerts, selectedOverlaidFlightLegId]);

  /**
   * Converts set of minutes of marginal service to bars for VictoryBar
   *
   * @param marginal is the set of minutes that marginal service will occur
   */
  const getMargServiceGraphData = useCallback(
    (marginal: number[]): { x: string; y: number; color: string }[] => {
      const marginalServiceBars: { x: string; y: number; color: string }[] = [];
      const serviceAlert =
        serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

      if (serviceAlert) {
        let size = 1;

        // Format data for VictoryBar
        marginal.forEach((curr, index) => {
          // Skip first element
          if (index) {
            const prev = marginal[index - 1];
            if (curr === prev + 1) {
              size++;
            } else {
              // Add yellow box for marginal service where x axis stays the same and y axis defines the length of the bar
              marginalServiceBars.push({
                x: INDEPENDENT_AXIS_DATA,
                y: size,
                color: appTheme.mptMarginalServiceColor,
              });

              // Add transparent bar that prevents no service/transmission bars from being overwritten
              marginalServiceBars.push({
                x: INDEPENDENT_AXIS_DATA,
                y: curr - prev - 1,
                color: 'transparent',
              });

              size = 1;
            }
          } else if (index === 0 && curr !== 0) {
            //First element
            marginalServiceBars.push({
              x: INDEPENDENT_AXIS_DATA,
              y: curr,
              color: 'transparent',
            });
          }
        });
        if (size - 1) {
          if (serviceAlert.durationMin === marginal[marginal.length - 1]) {
            marginalServiceBars.push({
              x: INDEPENDENT_AXIS_DATA,
              y: size - 1,
              color: appTheme.mptMarginalServiceColor,
            });
            marginalServiceBars.push({
              x: INDEPENDENT_AXIS_DATA,
              y: serviceAlert.durationMin - marginal[marginal.length - 1],
              color: 'transparent',
            });
          } else {
            marginalServiceBars.push({
              x: INDEPENDENT_AXIS_DATA,
              y: size,
              color: appTheme.mptMarginalServiceColor,
            });
            marginalServiceBars.push({
              x: INDEPENDENT_AXIS_DATA,
              y: serviceAlert.durationMin - marginal[marginal.length - 1] - 1,
              color: 'transparent',
            });
          }
        }
      }
      return marginalServiceBars;
    },
    [serviceAlerts, selectedOverlaidFlightLegId],
  );

  // Convert service alerts into graphable data and calculate tickInterval
  useEffect(() => {
    const serviceAlert: FlightLegServiceAlerts | undefined =
      serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

    if (serviceAlert?.serviceAlerts) {
      // Array that will be populated with numbers corresponding to the minutes that marginal service is in effect
      const margService = !isNil(currentMission?.takeOffBuffer) ? [...range(0, currentMission?.takeOffBuffer)] : [];
      // Array that will be populated with numbers corresponding to the minutes that there is no terminal transmission (no coverage and HNA zones)
      const noTransmission: number[] = [];
      // Half of handOffBuffer for calculating the start and end time of any handoff marginal service
      const halfHandOffBuffer = !isNil(currentMission.handOffBuffer) ? currentMission.handOffBuffer / 2 : 0;

      // Add new takeoff and landing service alerts AND remove BEFORE_HAND_OFF and AFTER_HAND_OFF service alerts
      const filteredServiceAlerts: (ServiceAlert | { timeSinceTakeOffMin: number })[] = [
        { timeSinceTakeOffMin: 0 },
        ...serviceAlert.serviceAlerts.slice().filter((serviceAlert: ServiceAlert) => {
          return (
            serviceAlert.type !== ServiceAlertType.BEFORE_HAND_OFF &&
            serviceAlert.type !== ServiceAlertType.AFTER_HAND_OFF &&
            serviceAlert.timeSinceTakeOffMin
          );
        }),
        {
          timeSinceTakeOffMin: serviceAlert.durationMin,
        },
      ];
      // Formats data for Victory charts and calculates marginal service regions and no transmission regions
      let barColor: string = '';
      const victoryData = filteredServiceAlerts.reduce(
        (
          summary: FlightLegChartData[],
          upcomingServiceAlert: ServiceAlert | { timeSinceTakeOffMin: number },
          index: number,
        ) => {
          if (index) {
            const currentAlert = filteredServiceAlerts[index - 1];
            const timeSinceTakeOffMin = currentAlert?.timeSinceTakeOffMin;
            const prevServiceAlert =
              !isNil(filteredServiceAlerts[index - 2]) && 'type' in filteredServiceAlerts[index - 2]
                ? (filteredServiceAlerts[index - 2] as ServiceAlert)
                : undefined;
            const alertDuration = upcomingServiceAlert.timeSinceTakeOffMin - timeSinceTakeOffMin;
            barColor = getSegmentColor(currentAlert);

            let label = `T+${minutesToFormattedMinutesStringColonDelimitedFormat(timeSinceTakeOffMin)}`;
            let userSetTime = undefined;

            if (!isNil(serviceAlerts) && serviceAlert.userSetTakeoffTimeMin > -1) {
              const minuteZulu = timeSinceTakeOffMin + serviceAlert.userSetTakeoffTimeMin;
              userSetTime = `${minutesToFormattedMinutesStringColonDelimitedFormat(minuteZulu % MINUTES_IN_DAY)}Z`;
            }

            summary.push({
              x: INDEPENDENT_AXIS_DATA,
              y: alertDuration,
              timeSinceTakeOffMin: `${timeSinceTakeOffMin}`,
              timestamp: label,
              userSetTimestamp: userSetTime,
              description:
                'type' in currentAlert
                  ? // @ts-ignore
                    getAlertDescription(currentAlert as ServiceAlert, serviceAlert.beams, prevServiceAlert)
                  : getTakeoffDescription(),
              color: barColor,
              type: 'type' in currentAlert ? (currentAlert as ServiceAlert).type : 'TAKEOFF',
            });

            // Add minutes that there is no transmission (no coverage or hna) to noTransmission array
            if (barColor !== getSegmentColorDefinitions().mptNominalServiceFlightPathColor) {
              const outerBound = !isNil(upcomingServiceAlert.timeSinceTakeOffMin)
                ? upcomingServiceAlert.timeSinceTakeOffMin === serviceAlert.durationMin
                  ? upcomingServiceAlert.timeSinceTakeOffMin + 1
                  : upcomingServiceAlert.timeSinceTakeOffMin
                : -1;

              const mins = range(timeSinceTakeOffMin, outerBound);
              mins.forEach(value => {
                // Add minute to noTransmission (no duplicates)
                if (!noTransmission.includes(value)) {
                  noTransmission.push(value);
                }
              });
            }

            // Add handoff marginal service minutes if it's not a Ka handoff (Ka handoffs are minimal) to margService array
            if (
              'type' in currentAlert &&
              (currentAlert as ServiceAlert).type === 'HAND_OFF' &&
              ((currentAlert as ServiceAlert).logoutBeamType !== 'VIASAT' ||
                (currentAlert as ServiceAlert).loginBeamType !== 'VIASAT')
            ) {
              const mins = range(timeSinceTakeOffMin - halfHandOffBuffer, timeSinceTakeOffMin + halfHandOffBuffer);
              mins.forEach(value => {
                if (!margService.includes(value)) {
                  margService.push(value);
                }
              });
            }
          }
          // Add landing service alert at the very end
          if (index === filteredServiceAlerts.length - 1) {
            const landingBuffer: number = !isNil(currentMission?.landingBuffer) ? currentMission.landingBuffer : 0;
            let label = `T+${minutesToFormattedMinutesStringColonDelimitedFormat(serviceAlert.durationMin)}`;
            let userSetTime = undefined;

            if (!isNil(serviceAlerts) && serviceAlert.userSetTakeoffTimeMin > -1) {
              const minuteZulu = serviceAlert.durationMin + serviceAlert.userSetTakeoffTimeMin;
              userSetTime = `${minutesToFormattedMinutesStringColonDelimitedFormat(minuteZulu % MINUTES_IN_DAY)}Z`;
            }

            summary.push({
              x: INDEPENDENT_AXIS_DATA,
              y: 0,
              timeSinceTakeOffMin: `${serviceAlert.durationMin}`,
              timestamp: label,
              userSetTimestamp: userSetTime,
              description: getLandingDescription(),
              color: barColor, // color of second to last bar
              type: 'LANDING',
            });

            // Add landing marginal service minutes to margService array
            const mins =
              landingBuffer !== 0 ? range(serviceAlert.durationMin - landingBuffer, serviceAlert.durationMin + 1) : [];
            mins.forEach(value => {
              if (!margService.includes(value)) {
                margService.push(value);
              }
            });
          }

          return summary;
        },
        [],
      );

      // Find all the nominal coverage areas within marginal service regions (this is where yellow bars will be placed on timeline)
      const nominal = difference(margService, noTransmission);

      // Updates graph data to include whether the alert has marginal service
      const updatedVictoryData: FlightLegChartData[] = [];
      victoryData?.forEach(data => {
        if (includes(nominal, parseInt(data.timeSinceTakeOffMin))) {
          updatedVictoryData.push({
            ...data,
            marginalService: true,
          });
        } else {
          updatedVictoryData.push(data);
        }
      });

      // Save chart data in store
      if (!isNil(selectedOverlaidFlightLegId)) {
        dispatch(setFlightLegVictoryChartData(selectedOverlaidFlightLegId, updatedVictoryData));
      }

      // Get marginal service bars to overlay above existing VictoryBar chart
      const marginalServiceBars = getMargServiceGraphData(nominal);
      setMarginalServiceData(marginalServiceBars);

      // Get tick interval
      const interval = getTickInterval();
      setTickInterval(interval);
    }
  }, [
    serviceAlerts,
    selectedOverlaidFlightLegId,
    currentMission.takeOffBuffer,
    currentMission.handOffBuffer,
    currentMission.landingBuffer,
    dispatch,
    getAlertDescription,
    getTakeoffDescription,
    getLandingDescription,
    getSegmentColor,
    getMargServiceGraphData,
    getTickInterval,
  ]);

  // On unmount
  useEffect(() => {
    return () => {
      // Clear chart data
      if (!isNil(selectedOverlaidFlightLegId)) {
        dispatch(deleteFlightLegVictoryChartData(selectedOverlaidFlightLegId));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const serviceAlert =
    serviceAlerts && selectedOverlaidFlightLegId ? serviceAlerts[selectedOverlaidFlightLegId] : undefined;

  return (
    <React.Fragment>
      {!isNil(serviceAlert) &&
        !isNil(graphData) &&
        !isNil(selectedOverlaidFlightLegId) &&
        !isNil(graphData[selectedOverlaidFlightLegId]) && (
          <Box
            sx={{
              position: 'relative',
              width: `${chartWidth}px`,
              // height: `${SCATTER_HEIGHT + BAR_HEIGHT}px`,
              '* > text': {
                tspan: {
                  '@media print': {
                    fill: '#000000 !important',
                  },
                },
              },
            }}
          >
            {/* Plot for Service Alert Icons */}
            <VictoryGroup width={chartWidth} height={SCATTER_HEIGHT}>
              {(graphData[selectedOverlaidFlightLegId] as FlightLegChartData[]).map((data, index) => {
                return (
                  <VictoryScatter
                    horizontal
                    height={SCATTER_HEIGHT}
                    key={data.timeSinceTakeOffMin}
                    data={[{ x: INDEPENDENT_AXIS_MARKER, y: parseInt(data.timeSinceTakeOffMin) }]}
                    labels={({ datum }) => [
                      data.timestamp + (data?.userSetTimestamp ? ` (${data.userSetTimestamp})` : ''),
                      trimDescription(data.description),
                    ]}
                    labelComponent={
                      <VictoryTooltip
                        orientation={'bottom'}
                        flyoutComponent={<LineAnnotation length={LINE_LENGTH} />}
                        labelComponent={
                          <VictoryLabel dy={62} textAnchor={'middle'} style={{ fill: appTheme.primaryTextColor }} />
                        }
                      />
                    }
                    dataComponent={
                      <ServiceAlertMarker
                        markerIndex={index}
                        flightLegServiceAlert={serviceAlert}
                        mapId={selectedMapId}
                      />
                    }
                    standalone={true}
                  />
                );
              })}
            </VictoryGroup>
            <VictoryChart
              horizontal
              width={chartWidth}
              height={BAR_HEIGHT}
              domainPadding={{ x: [60, 0] }}
              padding={{ top: 0, bottom: 50, left: 50, right: 50 }}
              containerComponent={
                <VictoryCursorContainer
                  cursorDimension="y"
                  cursorLabel={datum => {
                    if (!isNil(serviceAlert) && serviceAlert?.durationMin) {
                      // Translates datum.x (pixels) to number of minutes
                      const minuteFromPixel = Math.floor(
                        (((datum?.x || 25) - 25) / (chartWidth - STACKED_BARS_LENGTH_DX_CHART_WIDTH - 25)) *
                          serviceAlert.durationMin,
                      );

                      // Converts number of minutes to T+hh:mm
                      let label = `T+${minutesToFormattedMinutesStringColonDelimitedFormat(minuteFromPixel)}`;

                      // Adds zulu time if a user set takeoff time exists
                      if (serviceAlert.userSetTakeoffTimeMin > -1) {
                        const minuteZulu = minuteFromPixel + serviceAlert.userSetTakeoffTimeMin;
                        label += ` (${minutesToFormattedMinutesStringColonDelimitedFormat(
                          minuteZulu % MINUTES_IN_DAY,
                        )}Z)`;
                      }

                      return label;
                    }

                    return '';
                  }}
                  cursorLabelComponent={
                    <VictoryLabel
                      y={60}
                      dx={serviceAlert?.userSetTakeoffTimeMin === -1 ? 0 : -25}
                      style={{ fill: appTheme.primaryTextColor }}
                    />
                  }
                  cursorLabelOffset={{ x: -25, y: 0 }}
                  cursorComponent={<LineAnnotation length={LINE_LENGTH} />}
                  portalZIndex={0}
                  responsive={false}
                  disable={disableTimelineHover ? disableTimelineHover : false}
                />
              }
            >
              {/* Left Bar Label */}
              <VictoryPortal>
                <VictoryLabel
                  text={
                    !isNil(serviceAlert) && serviceAlert.userSetTakeoffTimeMin > -1
                      ? `T+00:00\n ${minutesToFormattedMinutesStringColonDelimitedFormat(
                          serviceAlert.userSetTakeoffTimeMin % MINUTES_IN_DAY,
                        )}Z`
                      : 'T+00:00'
                  }
                  x={-6}
                  y={6}
                  style={{
                    fill: appTheme.primaryTextColor,
                  }}
                />
              </VictoryPortal>
              {/* Right Bar Label */}
              {serviceAlert?.durationMin && (
                <VictoryPortal>
                  <VictoryLabel
                    text={
                      !isNil(serviceAlert) && serviceAlert.userSetTakeoffTimeMin > -1
                        ? `T+${minutesToFormattedMinutesStringColonDelimitedFormat(
                            serviceAlert?.durationMin as number,
                          )}\n ${minutesToFormattedMinutesStringColonDelimitedFormat(
                            (serviceAlert.userSetTakeoffTimeMin + serviceAlert.durationMin) % MINUTES_IN_DAY,
                          )}Z`
                        : `T+${minutesToFormattedMinutesStringColonDelimitedFormat(
                            serviceAlert?.durationMin as number,
                          )}`
                    }
                    x={chartWidth - STACKED_BARS_LENGTH_DX_CHART_WIDTH + 32}
                    y={6}
                    style={{
                      fill: appTheme.primaryTextColor,
                    }}
                  />
                </VictoryPortal>
              )}
              {/* Timeline */}
              <VictoryStack standalone={false}>
                {(graphData[selectedOverlaidFlightLegId] as FlightLegChartData[]).map(data => (
                  <VictoryBar
                    key={data.timeSinceTakeOffMin}
                    data={[data]}
                    style={{
                      data: { fill: data.color },
                    }}
                    barWidth={20}
                  />
                ))}
              </VictoryStack>
              {/* Overlaid Marginal Handoff Bars */}
              <VictoryStack standalone={false}>
                {marginalServiceData.map((data, index) => (
                  <VictoryBar key={index} data={[data]} style={{ data: { fill: data.color } }} barWidth={20} />
                ))}
              </VictoryStack>
              {/* X Axis */}
              <VictoryAxis
                dependentAxis
                tickComponent={<LineSegment y1={0} y2={17.5} />}
                labelComponent={<Box></Box>}
                tickValues={getTickIntervalsList()}
                tickFormat={tickValue => {
                  // Determine whether or not to have a label under the tick mark (blank versus e.g. 'T+00:00')
                  if (tickValue % (tickInterval === 5.0 ? 3 : 4) !== 0) {
                    return '';
                  }

                  let label = `T+${minutesToFormattedMinutesStringColonDelimitedFormat(tickValue)}`;

                  // Append zulu time if user has set a start time
                  if (!isNil(serviceAlert) && serviceAlert?.userSetTakeoffTimeMin > -1) {
                    const zuluTickValue = tickValue + serviceAlert.userSetTakeoffTimeMin;
                    label += `\n${minutesToFormattedMinutesStringColonDelimitedFormat(
                      zuluTickValue % MINUTES_IN_DAY,
                    )}Z`;
                  }
                  return label;
                }}
                offsetY={110}
                standalone={false}
                style={{
                  ticks: { stroke: appTheme.primaryTextColor },
                  tickLabels: {
                    fill: appTheme.primaryTextColor,
                  },
                  axis: { stroke: 'transparent' },
                }}
              />
              {/* Vertical Lines at Service Alert Icons */}
              {(graphData[selectedOverlaidFlightLegId] as FlightLegChartData[]).map(data => {
                const timeStamp = parseInt(data.timeSinceTakeOffMin);

                const xVal = serviceAlert?.durationMin
                  ? (timeStamp * (chartWidth - 100)) / serviceAlert?.durationMin + 50
                  : 9999999;

                const strokeColor =
                  timeStamp !== 0 && timeStamp !== serviceAlert?.durationMin
                    ? appTheme.primaryBackgroundColor
                    : 'transparent';

                return (
                  <LineSegment
                    key={xVal}
                    x1={xVal}
                    y1={0}
                    x2={xVal}
                    y2={17.5}
                    style={{ stroke: strokeColor, strokeWidth: 2 }}
                  />
                );
              })}
            </VictoryChart>
          </Box>
        )}
    </React.Fragment>
  );
};

export default PerformanceOutlookHorizontalTimeline;

const getEventListHnaAltitudeString = (
  loginBeamName: string | undefined,
  networkType: string | undefined,
  mapType: string | undefined,
  beams: FeatureCollection,
): string | null => {
  if (isNil(loginBeamName)) {
    return null;
  }

  const hnaAltitude = getAltitudeRestrictionForBeam(loginBeamName, networkType, mapType, beams);
  const hnaAltitudeString = getAltitudeString(hnaAltitude);

  //convert all except the first letter to lowercase
  const capitalized = hnaAltitudeString.charAt(0) + hnaAltitudeString.slice(1).toLowerCase();
  return capitalized;
};

//remove Altitude restriction string when hovering over service alert icon
const trimDescription = (description: string): string => {
  const index = description.indexOf('Altitude restriction');

  if (index === -1) {
    return description;
  }

  const updatedDescription = description.substring(0, index).trim();

  if (updatedDescription.charAt(updatedDescription.length - 1) === '(') {
    return updatedDescription.slice(0, -1);
  }

  return updatedDescription.concat(')');
};
